(() => {
  const KEY = 'ys_wishlist_handles_v1';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  };

  const uniq = (arr) => {
    const out = [];
    const seen = new Set();
    for (const h of arr) {
      const handle = String(h || '').trim();
      if (!handle || seen.has(handle)) continue;
      seen.add(handle);
      out.push(handle);
    }
    return out;
  };

  const rootUrl = () => {
    const r = window.Shopify?.routes?.root;
    if (typeof r === 'string' && r.length) return r.endsWith('/') ? r : (r + '/');
    return '/';
  };

  const productUrl = (handle) => `${rootUrl()}products/${handle}`;

  const escapeHtml = (s) =>
    String(s ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const formatMoney = (cents) => {
    const n = Number(cents || 0);

    // si le thème expose Shopify.formatMoney
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt =
        (window.theme && window.theme.moneyFormat) ||
        window.Shopify.money_format ||
        '${{amount}}';
      try {
        return window.Shopify.formatMoney(n, fmt);
      } catch (e) {}
    }

    // fallback Intl
    const currency =
      (window.Shopify?.currency && window.Shopify.currency.active) ? window.Shopify.currency.active : 'EUR';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency
      }).format(n / 100);
    } catch (e) {
      return (n / 100).toFixed(2) + ' ' + currency;
    }
  };

  // cache pour éviter de refetch à chaque changement
  const cache = new Map(); // handle -> product json
  let lastSignature = null;
  let renderToken = 0;

  async function fetchProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);

    const res = await fetch(`${productUrl(handle)}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    cache.set(handle, data);
    return data;
  }

  function cardHtml(p) {
    const handle = p.handle;
    const url = productUrl(handle);

    const title = escapeHtml(p.title || handle);
    const img = p.featured_image || (Array.isArray(p.images) && p.images[0]) || '';
    const price = formatMoney(p.price);
    const compare = p.compare_at_price && p.compare_at_price > p.price ? formatMoney(p.compare_at_price) : null;

    return `
      <div class="ys-wishlist-card" data-handle="${escapeHtml(handle)}">
        <a class="ys-wishlist-card__media" href="${escapeHtml(url)}">
          ${img ? `<img src="${escapeHtml(img)}" alt="${title}" loading="lazy">` : ``}
        </a>

        <button
          type="button"
          class="ys-wishlist-btn ys-wishlist-card__btn"
          aria-label="Ajouter aux favoris"
          aria-pressed="false"
          data-ys-wishlist
          data-handle="${escapeHtml(handle)}"
        >
          <span class="ys-wishlist-heart" aria-hidden="true">♡</span>
        </button>

        <div class="ys-wishlist-card__meta">
          <div>
            <a class="ys-wishlist-card__title" href="${escapeHtml(url)}">${title}</a>
            <div class="ys-wishlist-card__price">
              ${compare ? `<s style="opacity:.65;margin-right:8px;">${compare}</s>` : ``}
              <span>${price}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function getNodes() {
    const root = document.querySelector('[data-ys-wishlist-page]');
    if (!root) return null;

    return {
      root,
      grid: root.querySelector('[data-ys-wishlist-grid]'),
      empty: root.querySelector('[data-ys-wishlist-empty]')
    };
  }

  async function render() {
    const nodes = getNodes();
    if (!nodes || !nodes.grid || !nodes.empty) return;

    const token = ++renderToken;

    const handles = uniq(read());
    const signature = JSON.stringify(handles);
    if (signature === lastSignature && nodes.grid.childElementCount) {
      // rien à faire
      return;
    }
    lastSignature = signature;

    if (handles.length === 0) {
      nodes.grid.innerHTML = '';
      nodes.empty.hidden = false;
      return;
    }

    nodes.empty.hidden = true;

    // placeholder rapide
    nodes.grid.innerHTML = '';

    // fetch (concurrence limitée)
    const limit = 6;
    const results = [];
    let i = 0;

    async function worker() {
      while (i < handles.length) {
        const idx = i++;
        const h = handles[idx];
        try {
          const p = await fetchProduct(h);
          results[idx] = p;
        } catch (e) {
          results[idx] = null;
        }
        if (token !== renderToken) return; // abandon si nouveau render demandé
      }
    }

    const workers = Array.from({ length: Math.min(limit, handles.length) }, () => worker());
    await Promise.all(workers);

    if (token !== renderToken) return;

    const products = results.filter(Boolean);

    if (products.length === 0) {
      nodes.grid.innerHTML = '';
      nodes.empty.hidden = false;
      return;
    }

    nodes.grid.innerHTML = products.map(cardHtml).join('');

    // si ys-wishlist.js est présent, il “paint” tout seul.
    // fallback minimal : applique l’état actif (classe + ♥/♡) si besoin.
    const setHeartsFallback = () => {
      const current = uniq(read());
      nodes.root.querySelectorAll('[data-ys-wishlist]').forEach((btn) => {
        const h = String(btn.dataset.handle || '').trim();
        const active = current.includes(h);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.setAttribute('aria-label', active ? 'Retirer des favoris' : 'Ajouter aux favoris');
        const heart = btn.querySelector('.ys-wishlist-heart');
        if (heart) heart.textContent = active ? '♥' : '♡';
      });
    };
    setHeartsFallback();
  }

  // 1) rendu initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  } else {
    render();
  }

  // 2) update si un autre onglet modifie la wishlist
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) render();
  });

  // 3) update si tu dispatch un event (voir patch ci-dessous)
  document.addEventListener('ys:wishlist:change', render);

  // 4) fallback robuste (si tu ne patches pas ys-wishlist.js) :
  //    petite surveillance de la clé (léger, et évite “liste pas à jour”)
  let lastPoll = localStorage.getItem(KEY);
  setInterval(() => {
    const now = localStorage.getItem(KEY);
    if (now !== lastPoll) {
      lastPoll = now;
      render();
    }
  }, 800);
})();
